/**
 * Colour helpers for theme data: hex parsing, WCAG luminance/contrast, and the
 * mixing/shading the background painters use for rims and highlights.
 *
 * Theme colours are always hex strings: '#rgb', '#rrggbb' or '#rrggbbaa'.
 * Translucent colours are composited over a backdrop (white by default) before
 * luminance or contrast is measured, matching how the UI panels are drawn over
 * the bright sky.
 */

/** r, g, b in 0–255 (may be fractional after mixing); a in 0–1. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type ColorInput = string | Rgba;

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

export function isHexColor(s: string): boolean {
  return HEX_RE.test(s);
}

export function parseHexColor(hex: string): Rgba {
  if (!HEX_RE.test(hex)) throw new Error(`invalid hex colour "${hex}"`);
  const h = hex.slice(1);
  if (h.length === 3) {
    const [r, g, b] = [...h].map((c) => parseInt(c + c, 16)) as [number, number, number];
    return { r, g, b, a: 1 };
  }
  const byte = (i: number) => parseInt(h.slice(i, i + 2), 16);
  return { r: byte(0), g: byte(2), b: byte(4), a: h.length === 8 ? byte(6) / 255 : 1 };
}

const toRgba = (c: ColorInput): Rgba => (typeof c === 'string' ? parseHexColor(c) : c);

const byteHex = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');

/** '#rrggbb', or '#rrggbbaa' when the colour is not fully opaque. */
export function formatHexColor(c: Rgba): string {
  const rgb = `#${byteHex(c.r)}${byteHex(c.g)}${byteHex(c.b)}`;
  return c.a >= 1 ? rgb : rgb + byteHex(c.a * 255);
}

/** Source-over compositing of `fg` onto `bg` (straight, non-premultiplied alpha). */
export function compositeColor(fg: ColorInput, bg: ColorInput): Rgba {
  const f = toRgba(fg);
  const b = toRgba(bg);
  const a = f.a + b.a * (1 - f.a);
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const ch = (x: number, y: number) => (x * f.a + y * b.a * (1 - f.a)) / a;
  return { r: ch(f.r, b.r), g: ch(f.g, b.g), b: ch(f.b, b.b), a };
}

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

const linear = (v: number) => {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG 2.x relative luminance (0 = black, 1 = white). Alpha is composited over `backdrop` (white). */
export function relativeLuminance(c: ColorInput, backdrop: ColorInput = WHITE): number {
  const o = compositeColor(c, backdrop);
  return 0.2126 * linear(o.r) + 0.7152 * linear(o.g) + 0.0722 * linear(o.b);
}

/**
 * WCAG contrast ratio (1–21) of text colour `fg` on background `bg`.
 * `bg` is first composited over `backdrop` (white), then `fg` over that result.
 */
export function contrastRatio(fg: ColorInput, bg: ColorInput, backdrop: ColorInput = WHITE): number {
  const base = compositeColor(bg, backdrop);
  const text = compositeColor(fg, base);
  const l1 = relativeLuminance(text);
  const l2 = relativeLuminance(base);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Linear blend in sRGB space: t = 0 → a, t = 1 → b (alpha blends too). */
export function mixHexColors(a: ColorInput, b: ColorInput, t: number): string {
  const x = toRgba(a);
  const y = toRgba(b);
  const k = Math.min(1, Math.max(0, t));
  const ch = (p: number, q: number) => p + (q - p) * k;
  return formatHexColor({ r: ch(x.r, y.r), g: ch(x.g, y.g), b: ch(x.b, y.b), a: ch(x.a, y.a) });
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function rgbToHsl({ r, g, b }: Rgba): Hsl {
  const R = r / 255;
  const G = g / 255;
  const B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === R) h = (G - B) / d + (G < B ? 6 : 0);
  else if (max === G) h = (B - R) / d + 2;
  else h = (R - G) / d + 4;
  return { h: h / 6, s, l };
}

function hslToRgb({ h, s, l }: Hsl, a: number): Rgba {
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255, a };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    const u = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (u < 1 / 6) return p + (q - p) * 6 * u;
    if (u < 1 / 2) return q;
    if (u < 2 / 3) return p + (q - p) * (2 / 3 - u) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255, a };
}

/**
 * Cartoon shading. amount ∈ [−1, 1]: negative darkens (and slightly
 * saturates, so rims read as a richer version of the fill rather than grey),
 * positive lightens toward white. Hue is preserved; alpha is kept.
 */
export function shadeHexColor(c: ColorInput, amount: number): string {
  const rgba = toRgba(c);
  const hsl = rgbToHsl(rgba);
  const k = Math.min(1, Math.max(-1, amount));
  const out: Hsl =
    k < 0
      ? { h: hsl.h, s: Math.min(1, hsl.s * (1 - k * 0.25)), l: hsl.l * (1 + k) }
      : { h: hsl.h, s: hsl.s * (1 - k * 0.2), l: hsl.l + (1 - hsl.l) * k };
  return formatHexColor(hslToRgb(out, rgba.a));
}

/** Canvas/CSS 'rgba(r, g, b, a)' string, with the colour's own alpha multiplied by `alpha`. */
export function cssRgba(c: ColorInput, alpha = 1): string {
  const { r, g, b, a } = toRgba(c);
  const k = Math.min(1, Math.max(0, a * alpha));
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Math.round(k * 1000) / 1000})`;
}
